import AuthStore from "auth/AuthStore"
import { apiClient, apiClientSilent } from "api/trpc/trpc-client"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import Loader from "reusable/mobx-helpers/Loader"
import RegionRouterStore from "features/main/RegionRouterStore"
import { apiPublicClientSilent } from "api/trpc-public/trpc-client"
import { makeAutoObservable } from "mobx"
import { formHandler, FormHandlerConfig } from "reusable/mobx-store-utils/form-handlers/new/form-handler"
import { ValidEmailSchema } from "@gap/backend/util/validations/validation-email"
import { confirmSignIn, signIn, signOut } from "aws-amplify/auth"
import Debug from "reusable/utils/debug"
import { snackbarError, snackbarInfo } from "reusable/mui/snackbar/snackbar"
import { notNull } from "@gap/utils/utils"
import DateUtils from "@gap/utils/date-utils"
import { resolveShareTokenErrorStatus, shareTokenErrorSnackbarMessages, ShareTokenErrorStatus } from "./shareTokenError"

interface TokenInfo {
   advisorName?: string
   advisorOrgName?: string
   workItemName?: string
   docTypeName?: string
   dueDate?: string
   clientOrgId: string
   docId: string
   workItemId: string
}

interface AuthShareStoreProps {
   authStore: AuthStore
   token: string
   regionRouterStore: RegionRouterStore
}

export default class AuthShareStore {
   sp
   loader = new Loader(true)
   tokenInfo: TokenInfo | null = null
   status: 'authenticating' | 'ask-for-names' | ShareTokenErrorStatus = 'authenticating'
   fields = {
      email: '',
      firstName: '',
      lastName: ''
   }
   useDifferentEmail = false
   documentDoesNotExists = false

   constructor(sp: AuthShareStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get formHandler() {
      return formHandler(this.fields, {
         email: { type: 'string' },
         firstName: { type: 'string' },
         lastName: { type: 'string' }
      } satisfies FormHandlerConfig)
   }

   get canContinue() {
      return this.fields.firstName && this.fields.lastName
   }

   get introText() {
      if (!this.tokenInfo) return ''

      if (this.tokenInfo.docTypeName === 'feedback') {
         return `${this.tokenInfo.advisorName} from ${this.tokenInfo.advisorOrgName} has sent you a request to share feedback for your recent session - ${this.tokenInfo.workItemName}.`
      } else {
         if (this.tokenInfo.dueDate) {
            return `${this.tokenInfo.advisorName} from ${this.tokenInfo.advisorOrgName} has sent you ${this.tokenInfo.docTypeName} to complete${this.tokenInfo.dueDate && ` by ${this.tokenInfo.dueDate}`} before your upcoming session - ${this.tokenInfo.workItemName}.`
         } else {
            return `${this.tokenInfo.advisorName} from ${this.tokenInfo.advisorOrgName} has sent you ${this.tokenInfo.docTypeName} to complete before your upcoming session - ${this.tokenInfo.workItemName}.`
         }
      }
   }

   load = async () => {
      await this.loader.executeLoading(async () => {
         try {
            const tokenInfo = await (async () => {
               if (this.sp.authStore.isAuthenticated) {
                  // set user info
                  const [user, tokenInfo] = await Promise.all([
                     apiClientSilent.user.getMyUserInfo.query(),
                     apiClientSilent.docShare.ensureDocumentAccessFromToken.query({ token: this.sp.token })
                  ])
                  this.fields.firstName = user.firstName
                  this.fields.lastName = user.lastName
                  this.fields.email = user.email
                  return tokenInfo
               } else {
                  return await apiPublicClientSilent.docShare.getTokenInfo.query({ token: this.sp.token })
               }
            })()
            this.tokenInfo = {
               ...tokenInfo,
               docTypeName: tokenInfo.docTypeName.toLowerCase(),
               dueDate: tokenInfo.dueDate ? DateUtils.formatDate(new Date(tokenInfo.dueDate), 'date-full-day') : undefined
            }
         } catch (error: any) {
            if (this.showShareTokenError(error)) return

            if (error?.data?.stack.includes('GapError.notFound')) {
               this.documentDoesNotExists = true
               return
            }

            snackbarError('Something went wrong. Please try again.')
         }
      }, 'Preparing document')
   }

   useDifferentEmailClick = () => {
      this.fields.email = ''
      this.fields.firstName = ''
      this.fields.lastName = ''
      this.useDifferentEmail = true
   }

   resendCode = async () => {
      await this.loader.executeLoading(async () => {
         // Fake code. Should trigger a resend
         await confirmSignIn({ challengeResponse: '000000' })
         snackbarInfo('A new code has been sent to your email address')
      }, 'Sending code')
   }

   backToLogin = async () => {
      window.location.reload()
   }

   /** Renders the matching token failure screen and snackbar, reporting whether the error was a known token failure. */
   showShareTokenError = (error: any) => {
      const shareTokenErrorStatus = resolveShareTokenErrorStatus(error?.message)
      if (!shareTokenErrorStatus) return false

      this.status = shareTokenErrorStatus
      snackbarError(shareTokenErrorSnackbarMessages[shareTokenErrorStatus])
      return true
   }

   createUserFromToken = async () => {
      if (this.formHandler.validate(ValidEmailSchema)) {
         await this.loader.executeLoading(async () => {
            try {
               await apiPublicClientSilent.docShare.createUserFromToken.query({ token: this.sp.token, email: this.fields.email })
            } catch (error: any) {
               if (this.showShareTokenError(error)) return

               return snackbarError('Something went wrong. Please try again.')
            }

            await this.logUserIn()
         }, 'Authenticating')
      }
   }

   logUserIn = async () => {
      await this.loader.executeLoading(async () => {
         const response = await signIn({
            username: this.fields.email,
            options: {
               authFlowType: 'CUSTOM_WITHOUT_SRP'
            }
         })

         if (response.nextStep.signInStep !== "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
            Debug.log.auth('Email based login failed', response)
            return snackbarError('We could not start email verification for this address. Please request a new link.')
         }

         this.sp.authStore.status = 'verify with mfa code'
      }, 'Authenticating')
   }

   onVerify = async () => {
      await this.loader.executeLoading(async () => {
         await this.onSuccessfullVerification()
      }, 'Authenticating')
   }

   onSuccessfullVerification = async () => {
      await this.getAndSetTokens()

      if (!this.fields.firstName) {
         this.status = 'ask-for-names'
         return
      }

      this.navigateToDocument()
   }

   setNamesAndContinue = async () => {
      await this.loader.executeLoading(async () => {
         await apiClient.user.updateMyProfile.mutate({ firstName: this.fields.firstName, lastName: this.fields.lastName })
         await this.onSuccessfullVerification()
      }, 'Loading document')
   }

   navigateToDocument = () => {
      const docInfo = notNull(this.tokenInfo, 'Doc info not found')
      this.sp.regionRouterStore.navigate(`/org/${docInfo.clientOrgId}/documents/edit/${docInfo.docId}?workItemId=${docInfo.workItemId}`)
   }

   continue = async () => {
      if (this.sp.authStore.isAuthenticated) {
         if (this.useDifferentEmail) {
            await signOut()
            await this.createUserFromToken()
         } else {
            this.navigateToDocument()
         }
      } else {
         await this.createUserFromToken()
      }
   }

   getAndSetTokens = async () => {
      // The way our current guest system works is that we use tokens to access the guest features, not amplify.
      // This will be changed in the future, but for now we need to set the tokens for the user.
      // This means, get our access tokens. Signout from amplify. Set tokens
      const { tokens, userId, firstName, lastName } = await apiClient.docShare.getMyAccessTokens.query({ token: this.sp.token })
      this.fields.firstName = firstName
      this.fields.lastName = lastName
      await signOut()
      this.sp.authStore.setTokens(tokens, userId)
   }
}

export const { Provider: AuthShareStoreProvider, useProvidedStore: useAuthShareStore } = createStoreContext(AuthShareStore)
